import { useRef, type CSSProperties, type HTMLAttributes } from 'react';

import { Icon, type IconName } from '../core/Icon';
import { SectionLabel } from '../core/SectionLabel';
import { useTranslation } from '../data/translations';

const DOUBLE_TAP_MS = 350;

export interface ContextModeOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
  readonly icon: IconName;
  /** Classe de portée du mode (ex. `mode-elevation`) pour laisser l'accent de la carte suivre `tokens/modes.css`. */
  readonly scope?: string;
}

export interface ContextModeSelectorProps<Value extends string>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'onChange'> {
  readonly label?: string;
  readonly options: readonly ContextModeOption<Value>[];
  readonly value?: Value;
  readonly onChange?: (value: Value) => void;
  /** Double-clic sur un mode : sélectionne ET valide immédiatement, avec la valeur cliquée passée
   * explicitement (même pattern que `EmotionalStateSelector.onCommit`) — le bouton Valider reste
   * le chemin normal au simple clic (voir revue « ajustements UX / navigation / carte sport »). */
  readonly onCommit?: (value: Value) => void;
  readonly style?: CSSProperties;
}

/**
 * Sélecteur des 9 modes de contexte. Le mode porte la logique fonctionnelle : cette grille ne
 * fait que le déclarer, elle ne préjuge d'aucun impact émotionnel — voir `EmotionalStateSelector`
 * pour l'axe séparé des états. Chaque carte applique la classe de portée du mode pour hériter de
 * son accent (`tokens/modes.css`), sans jamais dépendre de la couleur seule (icône + libellé).
 */
export function ContextModeSelector<Value extends string>({
  label,
  options,
  value,
  onChange,
  onCommit,
  style,
  ...rest
}: ContextModeSelectorProps<Value>) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('mode.selector.label');
  /** Détection double-tap tactile (Android) + double-clic desktop : même mécanisme, seuil unique.
   *  `tapRef` mémorise la dernière valeur touchée et son timestamp ; `commitGate` empêche
   *  un double-déclenchement (click + dblclick / tap + double-tap) sur la même interaction. */
  const tapRef = useRef<{ value: Value; time: number } | null>(null);
  const commitGate = useRef(false);

  const handleTap = (nextValue: Value) => {
    if (commitGate.current) { commitGate.current = false; return; }
    onChange?.(nextValue);
    // eslint-disable-next-line react-hooks/purity -- appelé uniquement dans un gestionnaire d'événement (onClick), pas pendant le rendu
    const now = Date.now();
    const prev = tapRef.current;
    if (prev && prev.value === nextValue && now - prev.time < DOUBLE_TAP_MS) {
      commitGate.current = true;
      onCommit?.(nextValue);
      tapRef.current = null;
    } else {
      tapRef.current = { value: nextValue, time: now };
    }
  };

  const handleDoubleClick = (nextValue: Value) => {
    if (commitGate.current) { commitGate.current = false; return; }
    commitGate.current = true;
    onCommit?.(nextValue);
  };
  return (
    <div {...rest} role="radiogroup" aria-label={resolvedLabel} style={{ display: 'grid', gap: 10, ...style }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 10,
        }}
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => handleTap(option.value)}
              onDoubleClick={() => handleDoubleClick(option.value)}
              className={`ai-focusable ${option.scope ?? ''}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 10,
                padding: '14px 16px',
                minHeight: 'var(--hit-min)',
                borderRadius: 'var(--radius-card)',
                cursor: 'pointer',
                transition: 'var(--t-hover)',
                background: active ? 'var(--accent-soft)' : 'var(--surface-card)',
                border: `1px solid ${active ? 'var(--accent-line)' : 'rgba(255,255,255,.07)'}`,
                color: active ? 'var(--accent)' : 'var(--text-body)',
              }}
            >
              <Icon name={option.icon} size={20} />
              <span
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 'var(--tracking-label)',
                  textTransform: 'uppercase',
                }}
              >
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
      <SectionLabel size={9} tone="muted" as="p" style={{ margin: 0 }}>
        {t('mode.selector.footer')}
      </SectionLabel>
    </div>
  );
}
